import { verticalListSortingStrategy, type SortingStrategy } from '@dnd-kit/sortable';

/**
 * 地点头成组拖拽时，多个 sortable 行同属一块移动；默认 verticalListSortingStrategy
 * 只用 activeIndex 单行的 rect 高度，导致占位与其它行位移只有「一张卡」高。
 * 将 active 行在策略计算中的高度扩成「整块」连续行的高度，并让同块 follower 与 leader 使用相同 transform。
 */
export function createBundleAwareVerticalListSortingStrategy(
  itemIds: readonly string[],
  bundleIds: readonly string[] | null | undefined,
): SortingStrategy {
  return (args) => {
    const { activeIndex, index, rects } = args;
    if (!bundleIds || bundleIds.length <= 1 || activeIndex < 0) {
      return verticalListSortingStrategy(args);
    }

    const bundleIndices = bundleIds
      .map((id) => itemIds.indexOf(String(id)))
      .filter((i) => i >= 0)
      .sort((a, b) => a - b);

    if (bundleIndices.length <= 1) return verticalListSortingStrategy(args);
    if (bundleIndices[0] !== activeIndex) return verticalListSortingStrategy(args);
    for (let i = 1; i < bundleIndices.length; i++) {
      if (bundleIndices[i] !== bundleIndices[i - 1] + 1) {
        return verticalListSortingStrategy(args);
      }
    }

    const firstRect = rects[activeIndex];
    const lastIdx = bundleIndices[bundleIndices.length - 1]!;
    const lastRect = rects[lastIdx];
    if (!firstRect || !lastRect) return verticalListSortingStrategy(args);

    const combinedHeight = lastRect.top + lastRect.height - firstRect.top;
    const patchedRects = rects.slice();
    patchedRects[activeIndex] = {
      ...firstRect,
      height: combinedHeight,
      bottom: firstRect.top + combinedHeight,
    };

    const patchedArgs = { ...args, rects: patchedRects };
    const bundleSet = new Set(bundleIndices);

    if (bundleSet.has(index) && index !== activeIndex) {
      return verticalListSortingStrategy({ ...patchedArgs, index: activeIndex });
    }
    return verticalListSortingStrategy({ ...patchedArgs, index });
  };
}
